import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function SubjectRow({ s, confirmDelete, onEdit, onAskDelete, onConfirmDelete }) {
  return (
    <div className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`subject-row-${s.id}`}>
      <div className="flex items-center gap-3">
        <span className="font-mono-spec text-xs px-2 py-0.5 bg-[var(--parchment)] text-[var(--ink-soft)]">{s.code}</span>
        <span className="font-medium">{s.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
          onClick={onEdit}
          data-testid={`edit-subject-${s.id}`}
        >
          <Pencil size={13} /> Modifier
        </button>
        {confirmDelete ? (
          <button className="!py-1.5 !px-3 text-xs bg-[var(--late)] text-white" onClick={onConfirmDelete} data-testid={`confirm-delete-subject-${s.id}`}>
            Confirmer ?
          </button>
        ) : (
          <button className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 !text-[var(--late)]" onClick={onAskDelete} data-testid={`delete-subject-${s.id}`}>
            <Trash2 size={13} /> Supprimer
          </button>
        )}
      </div>
    </div>
  );
}

function SubjectDialog({ dialog, setDialog, onSave }) {
  return (
    <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            {dialog?.mode === "create" ? "Nouvelle matière" : "Modifier la matière"}
          </DialogTitle>
        </DialogHeader>
        {dialog && (
          <div className="space-y-3">
            <div>
              <label className="eyebrow block mb-1">Nom</label>
              <input className={inputCls} value={dialog.name} onChange={(e) => setDialog((s) => ({ ...s, name: e.target.value }))} placeholder="Ex. Espagnol" data-testid="subject-name-input" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Code (abréviation)</label>
              <input className={inputCls} value={dialog.code} onChange={(e) => setDialog((s) => ({ ...s, code: e.target.value }))} placeholder="Ex. ESP" data-testid="subject-code-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <button className="btn-secondary" onClick={() => setDialog(null)}>Annuler</button>
          <button className="btn-primary" onClick={onSave} data-testid="subject-save-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useSubjectsCrud() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/subjects");
    setSubjects(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!dialog.name.trim()) {
      toast.error("Nom de matière requis");
      return;
    }
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/subjects", { name: dialog.name, code: dialog.code });
        toast.success("Matière créée");
      } else {
        await api.put(`/admin/subjects/${dialog.id}`, { name: dialog.name, code: dialog.code });
        toast.success("Matière modifiée");
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/subjects/${id}`);
      toast.success("Matière supprimée");
      setConfirmId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { subjects, loading, dialog, setDialog, confirmId, setConfirmId, save, remove };
}

export default function SubjectsManager() {
  const { subjects, loading, dialog, setDialog, confirmId, setConfirmId, save, remove } = useSubjectsCrud();

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="subjects-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Matières enseignées</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">{subjects.length} matière{subjects.length > 1 ? "s" : ""}</h2>
        </div>
        <button
          className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
          onClick={() => setDialog({ mode: "create", name: "", code: "" })}
          data-testid="add-subject-btn"
        >
          <Plus size={15} /> Nouvelle matière
        </button>
      </div>

      {loading ? (
        <div className="mt-6 text-[var(--ink-soft)]">Chargement...</div>
      ) : (
        <div className="mt-4 divide-y divide-[var(--parchment-2)]" data-testid="subjects-list">
          {subjects.map((s) => (
            <SubjectRow
              key={s.id}
              s={s}
              confirmDelete={confirmId === s.id}
              onEdit={() => setDialog({ mode: "edit", id: s.id, name: s.name, code: s.code })}
              onAskDelete={() => setConfirmId(s.id)}
              onConfirmDelete={() => remove(s.id)}
            />
          ))}
          {subjects.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucune matière.</div>}
        </div>
      )}

      <SubjectDialog dialog={dialog} setDialog={setDialog} onSave={save} />
    </div>
  );
}
